import { cn } from '@/internationalization/cn'

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Required unless a visible <label> is wired to this control. */
  label: string
  /** Render the label text next to the switch instead of only for assistive tech. */
  showLabel?: boolean
  description?: string
  disabled?: boolean
  size?: 'sm' | 'md'
  className?: string
}

/**
 * A setting that takes effect immediately.
 *
 * If the change needs a Save button, use a Checkbox — a switch that does not
 * apply on flip teaches users the app is lying about state.
 */
export function Switch({
  checked,
  onChange,
  label,
  showLabel,
  description,
  disabled,
  size = 'md',
  className,
}: SwitchProps) {
  const dims = size === 'sm' ? { track: 'h-4 w-7', knob: 'h-3 w-3', shift: 'translate-x-3' } : { track: 'h-5 w-9', knob: 'h-4 w-4', shift: 'translate-x-4' }

  return (
    <div className={cn('flex items-start gap-3', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={showLabel ? undefined : label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex shrink-0 items-center rounded-full border border-transparent',
          'transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-50',
          dims.track,
          checked ? 'bg-brand' : 'bg-border-strong',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'ml-0.5 inline-block transform rounded-full bg-white shadow-xs transition-transform duration-fast',
            dims.knob,
            checked ? dims.shift : 'translate-x-0',
          )}
        />
      </button>
      {showLabel && (
        <span className="min-w-0">
          <span className="block text-base text-fg">{label}</span>
          {description && <span className="mt-0.5 block text-sm text-fg-subtle">{description}</span>}
        </span>
      )}
    </div>
  )
}
